import { staticFormApiConnectorLegacy } from 'apiConnector/form/static';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { commonFormActions } from 'state/forms/actions/form';
import { commonQuoteAction } from 'state/forms/actions/quote';
import type { Dispatchable, EpicWithThunk } from 'state/state.common';
import { defaultFormReceivedThunk } from 'state/static/staticForm.thunks';
import type { WorkspaceTabQuoteRepriced } from 'state/workspaces/workspace.actions';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

import type { ReloadStaticFormRequest } from './fetch-when-quote-reprice';

export function buildReloadStaticFormRequest(
  { tabId, id, idType, rfqId, pricerRfqId, processingChain }: WorkspaceTabQuoteRepriced,
  isPrivateMode: boolean,
) {
  const tradeDate = getTodayStringInLocalTimezone();
  return {
    tabId,
    id,
    idType,
    tradeDate,
    quoteId: rfqId,
    pricerRfqId,
    processingChain,
    isPrivateMode,
  } as ReloadStaticFormRequest;
}

export function fetchStaticFormWhenQuoteRepriceWithLastQuoteEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { oldHttpClient, selectors: { isUserPreferencesPrivateMode } }) =>
    action$.pipe(
      ofType('WORKSPACE_TAB_QUOTE_REPRICED'),
      filter(action => action.workspace === 'static'),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const { tabId, rfqId, withLastQuote } = action;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const api = staticFormApiConnectorLegacy(oldHttpClient);
        const isPrivateMode = isUserPreferencesPrivateMode(state);
        const request = buildReloadStaticFormRequest(action, isPrivateMode);

        return api
          .getStaticFormByIdType(request)
          .pipe(
            mergeMap(staticForm => {
              const {
                productView,
                values,
                blocksMetadata,
                fieldsMetadata,
                version,
                actions,
                quoteQuery,
                errors,
                negotiationId,
                mustForceAdvancedMode,
                allowedRequests,
              } = staticForm;

              const dispatchable: Dispatchable[] = [
                defaultFormReceivedThunk(
                  tabId,
                  productView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                ) as Dispatchable,
              ];

              if (withLastQuote) {
                dispatchable.push(commonQuoteAction.previousQuoteLoadRequested('static', tabId, rfqId));
              }

              return of(...dispatchable);
            }),
            catchError(error =>
              of(
                commonFormActions.defaultFormFailed('static', tabId, error.message || 'repriced failed', error.traceId),
              ),
            ),
            timeoutWith(timeoutInMillis, of(commonFormActions.defaultFormTimeouted('static', tabId)), appScheduler),
          )
          .pipe(takeUntil(tabRemoved$));
      }),
    );
}
